import { defineComponent, h, onMounted, ref, resolveComponent } from 'vue'
import { RouteLocation, RouterLink, useRoute } from 'vue-router'

import {
  CBadge,
  CNavGroup,
  CNavItem,
  CNavTitle,
  CSidebarNav,
} from '@coreui/vue-pro'
import { CIcon } from '@coreui/icons-vue'
import nav from '@/_nav'
import { AuthHelper } from '@/api_client'

type Badge = {
  color?: string
  text?: string
}

type Item = {
  badge?: Badge
  component: string
  icon: string
  items?: Item[]
  name?: string
  to?: string
}

const normalizePath = (path: string) =>
  decodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route: RouteLocation, link: string) => {
  if (link === undefined) {
    return false
  }

  if (route.hash === link) {
    return true
  }

  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)

  return currentPath === targetPath
}

const isActiveItem = (route: RouteLocation, item: Item): boolean => {
  if (item.to && isActiveLink(route, item.to)) {
    return true
  }

  if (item.items) {
    return item.items.some((child) => isActiveItem(route, child))
  }

  return false
}

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
  },
  created() {
    this.apiClient.getCustomer(AuthHelper.getUserId()).then((d) => {
      if (d.roles.length <= 1) {
        AuthHelper.removeOAuthToken()
      } else {
        AuthHelper.setRoles(d.roles)
      }
    })
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setup() {
    const route = useRoute()
    const firstRender = ref(true)
    onMounted(() => {
      firstRender.value = false
    })

    const renderItem = (item: Item, isChild = false) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child)),
            }),
          },
          {
            togglerContent: () => [
              h(CIcon, {
                customClassName: 'nav-icon',
                name: item.icon,
              }),
              item.name,
            ],
            default: () =>
              item.items &&
              item.items
                .filter(
                  (i) =>
                    AuthHelper.isAuthorized(i.name) ||
                    (i.name === 'Home sections' &&
                      AuthHelper.getUserEmail() === 'nhu@secondcorner.vn'),
                )
                .map((child) => renderItem(child, true)),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              default: (props: any) =>
                h(
                  resolveComponent(item.component),
                  {
                    class: isChild ? ' nav-child' : '',
                    active: props.isActive,
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  () => [
                    item.icon &&
                      h(CIcon, {
                        customClassName: 'nav-icon ',
                        name: item.icon,
                      }),
                    item.name,
                    item.badge &&
                      h(
                        CBadge,
                        {
                          class: 'ms-auto',
                          color: item.badge.color,
                        },
                        () => item.badge && item.badge.text,
                      ),
                  ],
                ),
            },
          )
        : h(resolveComponent(item.component), {}, () => item.name)
    }

    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return nav
              .filter((i) => AuthHelper.isAuthorized(i.name))
              .map((item) => {
                return renderItem(item as Item)
              })
          },
        },
      )
  },
})

export { AppSidebarNav }
